import {
  Group, Mesh,
  CylinderGeometry, ConeGeometry,
  MeshLambertMaterial, MeshPhongMaterial,
} from 'three';
import { quad, buildGeo as mkGeo } from './ModelUtils';

/**
 * EF-4 イーグルファング — F-14 トムキャット風
 *
 * SF-0 と同じ断面押し出し方式。
 * 断面は8角形:
 *
 *         T  (top center — スパイン)
 *        / \
 *      TL   TR  (上面肩)
 *     /       \
 *   OL         OR  (外壁 — 最大幅)
 *     \       /
 *      BL   BR  (下面)
 *        \ /
 *         B  (bottom center)
 */
export interface Gen4ColorScheme {
  body: number;
  dark: number;
  under: number;
  nozzleEmissive?: number;
}

export const GEN4_DEFAULT_COLORS: Gen4ColorScheme = {
  body: 0x6a7a8a, dark: 0x3a4a52, under: 0x4a5a62,
};

export const GEN4_ENEMY_COLORS: Gen4ColorScheme = {
  body: 0x8a2222, dark: 0x551818, under: 0x6a1a1a, nozzleEmissive: 0xff2200,
};

export function buildGen4Model(colors: Gen4ColorScheme = GEN4_DEFAULT_COLORS): Group {
  const root = new Group();

  const matBody   = new MeshPhongMaterial({ color: colors.body, shininess: 30, flatShading: true });
  const matDark   = new MeshPhongMaterial({ color: colors.dark, shininess: 20, flatShading: true });
  const matUnder  = new MeshPhongMaterial({ color: colors.under, shininess: 15, flatShading: true });
  const matCanopy = new MeshPhongMaterial({
    color: 0x182838, emissive: 0x000c18, shininess: 250,
    transparent: true, opacity: 0.78,
  });
  const matNozzle = new MeshPhongMaterial({
    color: 0x252528, emissive: colors.nozzleEmissive ?? 0x100600, emissiveIntensity: 0.3, shininess: 100,
  });
  const matIntake = new MeshLambertMaterial({ color: 0x0e0e12 });

  // ── 断面ステーション定義 ──
  // tw=スパイン半幅, ty=スパイン高さ
  // sw=肩幅, sy=肩高さ
  // ow=外壁幅(最大), oy=外壁高さ
  // bw=腹幅, by=腹高さ, boy=底高さ
  type Sta = {
    z: number;
    tw: number; ty: number;   // T (spine)
    sw: number; sy: number;   // TL/TR (shoulder)
    ow: number; oy: number;   // OL/OR (outer wall)
    bw: number; by_: number;  // BL/BR (belly)
    boy: number;              // B (bottom)
  };

  const stations: Sta[] = [
    //              z     tw    ty    sw    sy    ow    oy    bw    by    boy
    { z: -13.0, tw: 0.00, ty: 0.05, sw: 0.02, sy: 0.03, ow: 0.03, oy: 0.00, bw: 0.02, by_: -0.03, boy: -0.05 },
    { z: -10.0, tw: 0.15, ty: 0.28, sw: 0.30, sy: 0.18, ow: 0.40, oy: 0.05, bw: 0.30, by_: -0.10, boy: -0.15 },
    { z:  -7.0, tw: 0.25, ty: 0.35, sw: 0.50, sy: 0.25, ow: 0.70, oy: 0.08, bw: 0.55, by_: -0.15, boy: -0.20 },
    { z:  -4.0, tw: 0.30, ty: 0.38, sw: 0.65, sy: 0.28, ow: 0.90, oy: 0.08, bw: 0.75, by_: -0.22, boy: -0.28 },
    { z:  -2.0, tw: 0.30, ty: 0.42, sw: 0.70, sy: 0.30, ow: 1.20, oy: 0.08, bw: 1.00, by_: -0.28, boy: -0.32 },
    { z:   0.0, tw: 0.25, ty: 0.48, sw: 0.80, sy: 0.38, ow: 1.80, oy: 0.10, bw: 1.50, by_: -0.30, boy: -0.35 },
    { z:   2.0, tw: 0.25, ty: 0.48, sw: 0.80, sy: 0.38, ow: 1.80, oy: 0.10, bw: 1.50, by_: -0.30, boy: -0.35 },
    { z:   4.0, tw: 0.25, ty: 0.46, sw: 0.75, sy: 0.36, ow: 1.80, oy: 0.08, bw: 1.50, by_: -0.30, boy: -0.35 },
    { z:   6.5, tw: 0.20, ty: 0.40, sw: 0.60, sy: 0.30, ow: 1.50, oy: 0.05, bw: 1.30, by_: -0.25, boy: -0.30 },
    { z:   9.0, tw: 0.15, ty: 0.32, sw: 0.45, sy: 0.24, ow: 1.20, oy: 0.02, bw: 1.05, by_: -0.20, boy: -0.25 },
  ];

  // 断面 → 8頂点 [T, TR, OR, BR, B, BL, OL, TL]
  function secV(s: Sta): number[][] {
    return [
      [0,      s.ty,  s.z],  // 0: T  (top/spine)
      [ s.sw,  s.sy,  s.z],  // 1: TR (shoulder right)
      [ s.ow,  s.oy,  s.z],  // 2: OR (outer right)
      [ s.bw,  s.by_, s.z],  // 3: BR (belly right)
      [0,      s.boy, s.z],  // 4: B  (bottom)
      [-s.bw,  s.by_, s.z],  // 5: BL (belly left)
      [-s.ow,  s.oy,  s.z],  // 6: OL (outer left)
      [-s.sw,  s.sy,  s.z],  // 7: TL (shoulder left)
    ];
  }

  // ── 胴体上面 (T→TR→OR + OL→TL→T) ──
  {
    const v: number[] = [], n: number[] = [];
    for (let i = 0; i < stations.length - 1; i++) {
      const a = secV(stations[i]);
      const b = secV(stations[i + 1]);
      // 上半分: T→TR, TR→OR, OL→TL, TL→T (4面)
      // 下半分は別マテリアル
      quad(v, n, a[0][0],a[0][1],a[0][2], b[0][0],b[0][1],b[0][2], b[1][0],b[1][1],b[1][2], a[1][0],a[1][1],a[1][2]); // T→TR
      quad(v, n, a[1][0],a[1][1],a[1][2], b[1][0],b[1][1],b[1][2], b[2][0],b[2][1],b[2][2], a[2][0],a[2][1],a[2][2]); // TR→OR
      quad(v, n, a[6][0],a[6][1],a[6][2], b[6][0],b[6][1],b[6][2], b[7][0],b[7][1],b[7][2], a[7][0],a[7][1],a[7][2]); // OL→TL
      quad(v, n, a[7][0],a[7][1],a[7][2], b[7][0],b[7][1],b[7][2], b[0][0],b[0][1],b[0][2], a[0][0],a[0][1],a[0][2]); // TL→T
    }
    root.add(new Mesh(mkGeo(v, n), matBody));
  }

  // ── 胴体外壁 (OR→BR + BL→OL) ──
  {
    const v: number[] = [], n: number[] = [];
    for (let i = 0; i < stations.length - 1; i++) {
      const a = secV(stations[i]);
      const b = secV(stations[i + 1]);
      quad(v, n, a[2][0],a[2][1],a[2][2], b[2][0],b[2][1],b[2][2], b[3][0],b[3][1],b[3][2], a[3][0],a[3][1],a[3][2]); // OR→BR
      quad(v, n, a[5][0],a[5][1],a[5][2], b[5][0],b[5][1],b[5][2], b[6][0],b[6][1],b[6][2], a[6][0],a[6][1],a[6][2]); // BL→OL
    }
    root.add(new Mesh(mkGeo(v, n), matDark));
  }

  // ── 胴体下面 (BR→B→BL) ──
  {
    const v: number[] = [], n: number[] = [];
    for (let i = 0; i < stations.length - 1; i++) {
      const a = secV(stations[i]);
      const b = secV(stations[i + 1]);
      quad(v, n, a[3][0],a[3][1],a[3][2], b[3][0],b[3][1],b[3][2], b[4][0],b[4][1],b[4][2], a[4][0],a[4][1],a[4][2]); // BR→B
      quad(v, n, a[4][0],a[4][1],a[4][2], b[4][0],b[4][1],b[4][2], b[5][0],b[5][1],b[5][2], a[5][0],a[5][1],a[5][2]); // B→BL
    }
    root.add(new Mesh(mkGeo(v, n), matUnder));
  }

  // ── キャノピー (胴体上面に載るバブル) ──
  {
    const v: number[] = [], n: number[] = [];
    const cp = [
      { z: -7.0, w: 0.0,  h: 0.0 },
      { z: -5.8, w: 0.42, h: 0.45 },
      { z: -4.8, w: 0.45, h: 0.40 },
      { z: -4.0, w: 0.30, h: 0.15 },
    ];
    for (let i = 0; i < cp.length - 1; i++) {
      const a = cp[i], b_ = cp[i + 1];
      const ayBase = 0.34, byBase = 0.34;
      const ayTop = ayBase + a.h, byTop = byBase + b_.h;
      // 上面
      quad(v, n, -a.w, ayTop, a.z, -b_.w, byTop, b_.z, b_.w, byTop, b_.z, a.w, ayTop, a.z);
      // 左面
      quad(v, n, -a.w, ayBase, a.z, -b_.w, byBase, b_.z, -b_.w, byTop, b_.z, -a.w, ayTop, a.z);
      // 右面
      quad(v, n, a.w, ayTop, a.z, b_.w, byTop, b_.z, b_.w, byBase, b_.z, a.w, ayBase, a.z);
    }
    root.add(new Mesh(mkGeo(v, n), matCanopy));
  }

  // ── 主翼 (大型後退翼) ──
  for (const sx of [-1, 1]) {
    const v: number[] = [], n: number[] = [];
    const rootLE_x = sx * 1.8, rootLE_z = 0.5;
    const rootTE_x = sx * 1.8, rootTE_z = 4.0;
    const tipLE_x  = sx * 7.0, tipLE_z  = 3.0;
    const tipTE_x  = sx * 6.0, tipTE_z  = 4.5;
    const rootY = 0.0, tipY = -0.02;
    const thick = 0.08, tipThick = 0.04;

    // 上面
    quad(v, n,
      rootLE_x, rootY+thick, rootLE_z,
      tipLE_x,  tipY+tipThick, tipLE_z,
      tipTE_x,  tipY+tipThick, tipTE_z,
      rootTE_x, rootY+thick, rootTE_z,
    );
    // 下面
    quad(v, n,
      rootLE_x, rootY-thick, rootLE_z,
      rootTE_x, rootY-thick, rootTE_z,
      tipTE_x,  tipY-tipThick, tipTE_z,
      tipLE_x,  tipY-tipThick, tipLE_z,
    );
    // 前縁
    quad(v, n,
      rootLE_x, rootY-thick, rootLE_z,
      tipLE_x,  tipY-tipThick, tipLE_z,
      tipLE_x,  tipY+tipThick, tipLE_z,
      rootLE_x, rootY+thick, rootLE_z,
    );
    // 後縁
    quad(v, n,
      rootTE_x, rootY+thick, rootTE_z,
      tipTE_x,  tipY+tipThick, tipTE_z,
      tipTE_x,  tipY-tipThick, tipTE_z,
      rootTE_x, rootY-thick, rootTE_z,
    );
    // 翼端
    quad(v, n,
      tipLE_x, tipY+tipThick, tipLE_z,
      tipLE_x, tipY-tipThick, tipLE_z,
      tipTE_x, tipY-tipThick, tipTE_z,
      tipTE_x, tipY+tipThick, tipTE_z,
    );
    root.add(new Mesh(mkGeo(v, n), matBody));

    // パイロン + ミサイル
    const py = new Mesh(new CylinderGeometry(0.05, 0.05, 0.3, 4), matDark);
    py.position.set(sx * 4.0, -0.20, 2.8);
    root.add(py);
    const ml = new Mesh(new CylinderGeometry(0.08, 0.08, 1.8, 6), matDark);
    ml.rotation.x = Math.PI / 2;
    ml.position.set(sx * 4.0, -0.42, 2.8);
    root.add(ml);
    const mn = new Mesh(new ConeGeometry(0.08, 0.35, 6), matDark);
    mn.rotation.x = Math.PI / 2;
    mn.position.set(sx * 4.0, -0.42, 1.75);
    root.add(mn);
  }

  // ── 水平尾翼 ──
  for (const sx of [-1, 1]) {
    const v: number[] = [], n: number[] = [];
    const rle_x = sx * 1.2, rle_z = 6.0;
    const rte_x = sx * 1.2, rte_z = 8.0;
    const tle_x = sx * 4.0, tle_z = 8.0;
    const tte_x = sx * 3.2, tte_z = 9.0;
    const y = 0.0, t = 0.05;

    quad(v, n, rle_x, y+t, rle_z, tle_x, y+t, tle_z, tte_x, y+t, tte_z, rte_x, y+t, rte_z);
    quad(v, n, rle_x, y-t, rle_z, rte_x, y-t, rte_z, tte_x, y-t, tte_z, tle_x, y-t, tle_z);
    quad(v, n, rle_x, y-t, rle_z, tle_x, y-t, tle_z, tle_x, y+t, tle_z, rle_x, y+t, rle_z);
    quad(v, n, rte_x, y+t, rte_z, tte_x, y+t, tte_z, tte_x, y-t, tte_z, rte_x, y-t, rte_z);

    root.add(new Mesh(mkGeo(v, n), matDark));
  }

  // ── 双垂直尾翼 (外傾) ──
  for (const sx of [-1, 1]) {
    const v: number[] = [], n: number[] = [];
    const rb_x = sx * 0.9, rb_z = 4.5, rb_y = 0.36;
    const rt_x = sx * 1.0, rt_z = 8.0, rt_y = 0.36;
    const cantOff = sx * 0.8;
    const tb_x = rb_x + cantOff, tb_z = 5.5, tb_y = 2.8;
    const tt_x = rt_x + cantOff, tt_z = 7.5, tt_y = 2.2;
    const t = 0.08;

    // 外面
    quad(v, n, rb_x+sx*t, rb_y, rb_z, rt_x+sx*t, rt_y, rt_z, tt_x+sx*t, tt_y, tt_z, tb_x+sx*t, tb_y, tb_z);
    // 内面
    quad(v, n, rt_x-sx*t, rt_y, rt_z, rb_x-sx*t, rb_y, rb_z, tb_x-sx*t, tb_y, tb_z, tt_x-sx*t, tt_y, tt_z);
    // 前縁
    quad(v, n, rb_x-sx*t, rb_y, rb_z, rb_x+sx*t, rb_y, rb_z, tb_x+sx*t, tb_y, tb_z, tb_x-sx*t, tb_y, tb_z);
    // 後縁
    quad(v, n, rt_x+sx*t, rt_y, rt_z, rt_x-sx*t, rt_y, rt_z, tt_x-sx*t, tt_y, tt_z, tt_x+sx*t, tt_y, tt_z);
    // 先端
    quad(v, n, tb_x-sx*t, tb_y, tb_z, tb_x+sx*t, tb_y, tb_z, tt_x+sx*t, tt_y, tt_z, tt_x-sx*t, tt_y, tt_z);

    root.add(new Mesh(mkGeo(v, n), matDark));
  }

  // ── エンジンノズル (双発) ──
  for (const sx of [-1, 1]) {
    const nx = sx * 0.85;
    const sh = new Mesh(new CylinderGeometry(0.42, 0.48, 2.0, 12), matDark);
    sh.rotation.x = Math.PI / 2;
    sh.position.set(nx, 0.0, 8.5);
    root.add(sh);
    const nz = new Mesh(new CylinderGeometry(0.32, 0.40, 0.5, 12), matNozzle);
    nz.rotation.x = Math.PI / 2;
    nz.position.set(nx, 0.0, 9.5);
    root.add(nz);
    const inn = new Mesh(new CylinderGeometry(0.25, 0.25, 0.3, 12), matIntake);
    inn.rotation.x = Math.PI / 2;
    inn.position.set(nx, 0.0, 9.6);
    root.add(inn);
  }

  // ── ピトー管 ──
  const pt = new Mesh(new CylinderGeometry(0.02, 0.02, 1.2, 4), matDark);
  pt.rotation.x = Math.PI / 2;
  pt.position.set(0, 0.08, -13.8);
  root.add(pt);

  return root;
}
